/*
 * Slideshow (sections/slideshow-new.liquid) video enhancements.
 * Section-scoped: hooks the public `slideInstance` of the theme's <slide-show>
 * element, core theme.js is untouched.
 *
 * - Mute / unmute toggle on video slides (.slideshow-item__mute)
 * - Keeps the visitor's unmute choice when the slideshow moves on, because
 *   theme.js re-mutes every video on each slide change.
 * - Auto-rotate waits for videos: while a video slide is active the Splide
 *   timer is paused, the video plays once (loop off) and the slideshow moves
 *   on when it ends. Image slides keep the normal "change slides every" timer.
 * - Seamless loop wrap: when Splide animates onto a clone slide (loop mode),
 *   the clone's video and the real slide's video are both parked on frame 0,
 *   so the silent swap from clone to real slide does not visibly jump.
 */
(function () {
  function initSlideshow(el) {
    if (el.dataset.slideshowVideoInit === 'true') return;
    el.dataset.slideshowVideoInit = 'true';

    var wantSound = false;

    function videoFor(button) {
      var slide = button.closest('.slideshow-item');
      return slide ? slide.querySelector('video') : null;
    }

    function syncButton(button) {
      var video = videoFor(button);
      var muted = !video || video.muted;
      button.classList.toggle('is-muted', muted);
      button.classList.toggle('is-unmuted', !muted);
      button.setAttribute('aria-pressed', muted ? 'false' : 'true');
      button.setAttribute('aria-label', muted ? 'Unmute video' : 'Mute video');
    }

    function syncAll() {
      el.querySelectorAll('.slideshow-item__mute').forEach(function (button) {
        var video = videoFor(button);
        if (video && video.dataset.muteSync !== 'true') {
          video.dataset.muteSync = 'true';
          video.addEventListener('volumechange', function () { syncButton(button); });
        }
        syncButton(button);
      });
    }

    var soundVideo = null; // the only video allowed to play with sound

    // viewport-media (theme.js) forces muted=true when a video finishes loading,
    // so re-apply the visitor's choice once data is available.
    function unmute(video) {
      soundVideo = video;
      video.muted = false;
      if (video.readyState < 2) {
        video.addEventListener('loadeddata', function () {
          if (wantSound && soundVideo === video) video.muted = false;
        }, { once: true });
      }
    }

    // Splide loop mode clones the video slides and theme.js never mutes or pauses
    // the clones, so silence every video except the one that may have sound.
    function muteOthers(keep) {
      el.querySelectorAll('video').forEach(function (video) {
        if (video !== keep) video.muted = true;
      });
    }

    // The original (non-clone) slide for the current index.
    function activeVideo(splide) {
      var current = splide.Components.Slides.getAt(splide.index);
      return current && current.slide ? current.slide.querySelector('video') : null;
    }

    function applyChoiceToActive(splide) {
      var video = activeVideo(splide);
      muteOthers(video);
      if (wantSound && video) unmute(video);
    }

    el.addEventListener('click', function (event) {
      var button = event.target.closest('.slideshow-item__mute');
      if (!button || !el.contains(button)) return;
      event.preventDefault();
      event.stopPropagation();
      var video = videoFor(button);
      if (!video) return;
      wantSound = video.muted;
      if (wantSound) {
        muteOthers(video);
        unmute(video);
        if (video.paused) video.play().catch(function () {});
      } else {
        soundVideo = null;
        video.muted = true;
      }
      syncAll();
    });

    // Runs after theme.js's own `moved` handler (registered earlier), which mutes all videos.
    function bindSplide(attempt) {
      var splide = el.slideInstance;
      if (!splide || !splide.on) {
        if (attempt < 30) setTimeout(function () { bindSplide(attempt + 1); }, 100);
        return;
      }
      splide.on('moved', function () {
        applyChoiceToActive(splide);
        syncAll();
        watchActiveVideo(splide);
      });
      // `dest` differs from `index` only when the transition runs onto a clone.
      splide.on('move', function (index, prev, dest) {
        if (dest === index) return;
        alignForWrap(splide, index);
      });
      muteOthers(activeVideo(splide));
      watchActiveVideo(splide);
    }

    function rewind(video) {
      if (!video) return;
      video.pause();
      try { video.currentTime = 0; } catch (e) { /* not seekable yet */ }
    }

    // Park the clone shown during the wrap and the real target slide on the same frame.
    function alignForWrap(splide, index) {
      el.querySelectorAll('.splide__slide--clone[data-splide-index="' + index + '"] video').forEach(rewind);
      var target = splide.Components.Slides.getAt(index);
      if (target && target.slide) rewind(target.slide.querySelector('video'));
    }

    /* ---- auto-rotate waits for the active video ---- */
    var autoRotate = el.getAttribute('data-auto-rotate') === 'true';
    var watched = null;      // { video, onEnded }
    var pendingObserver = null;
    var fallbackTimer = null;

    function autoplay(splide) {
      return splide.Components && splide.Components.Autoplay;
    }

    function resumeTimer(splide) {
      var ap = autoplay(splide);
      if (!ap || !autoRotate) return;
      // Respect the theme's pause-on-hover setting.
      if (splide.options.pauseOnHover && el.matches(':hover')) return;
      if (ap.isPaused()) ap.play();
    }

    function stopWatching() {
      if (watched) {
        watched.video.removeEventListener('ended', watched.onEnded);
        watched = null;
      }
      if (pendingObserver) { pendingObserver.disconnect(); pendingObserver = null; }
      if (fallbackTimer) { clearTimeout(fallbackTimer); fallbackTimer = null; }
    }

    function watchVideo(splide, video) {
      stopWatching();
      var ap = autoplay(splide);
      if (ap) ap.pause();
      video.loop = false;
      var onEnded = function () {
        stopWatching();
        splide.go('>');
      };
      video.addEventListener('ended', onEnded);
      watched = { video: video, onEnded: onEnded };
      if (video.ended) { onEnded(); return; }
      // If the browser refuses to play (or the video stalls), fall back to the normal timer.
      fallbackTimer = setTimeout(function () {
        if (watched && watched.video === video && video.paused && !video.ended) {
          stopWatching();
          resumeTimer(splide);
        }
      }, 3000);
    }

    function watchActiveVideo(splide) {
      if (!autoRotate) return;
      stopWatching();
      var current = splide.Components.Slides.getAt(splide.index);
      var slide = current && current.slide;
      if (!slide) return;
      var video = slide.querySelector('video');
      if (video) { watchVideo(splide, video); return; }
      var media = slide.querySelector('viewport-media');
      if (media && 'MutationObserver' in window) {
        // Video is injected by viewport-media once the slide is near the viewport.
        pendingObserver = new MutationObserver(function () {
          var v = slide.querySelector('video');
          if (v) { pendingObserver.disconnect(); pendingObserver = null; watchVideo(splide, v); }
        });
        pendingObserver.observe(media, { childList: true });
        fallbackTimer = setTimeout(function () {
          if (pendingObserver) { stopWatching(); resumeTimer(splide); }
        }, 3000);
        return;
      }
      resumeTimer(splide);
    }

    bindSplide(0);
    syncAll();
  }

  function init(root) {
    (root || document).querySelectorAll('slide-show').forEach(initSlideshow);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
